import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, Search, ChevronDown, Check } from "lucide-react";

const DATE_RANGE_OPTIONS = [
    { key: "today",         label: "Today" },
    { key: "yesterday",     label: "Yesterday" },
    { key: "last_7_days",   label: "Last 7 days" },
    { key: "last_15_days",  label: "Last 15 days" },
    { key: "last_30_days",  label: "Last 30 days" },
    { key: "last_60_days",  label: "Last 60 days" },
    { key: "last_90_days",  label: "Last 90 days" },
    { key: "mtd",           label: "Month-to-date" },
    { key: "last_month",    label: "Last month" },
    { key: "custom",        label: "Custom range" },
];

const emptyForm = () => ({
    name: "",
    platform: "meta",
    ad_account_ids: [],
    campaign_ids: [],
    metrics: [],
    breakdowns: ["none"],
    date_range_type: "last_7_days",
    custom_start_date: "",
    custom_end_date: "",
    formats: ["csv"],
    visibility: "private",
});

export default function TemplateEditorModal({ templateId, onClose, onSaved, currentUser }) {
    const isEdit = !!templateId;
    const [form, setForm] = useState(emptyForm);
    const [catalog, setCatalog] = useState(null);       // { platform, metrics, breakdowns, ... }
    const [adAccounts, setAdAccounts] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Load platform catalog
    const loadCatalog = useCallback(async (platform) => {
        try {
            const { data } = await api.get(`/report-templates/catalog?platform=${platform}`);
            setCatalog(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load catalog");
        }
    }, []);

    // Load ad-accounts + campaigns for scope
    const loadScope = useCallback(async (platform, aaIds = []) => {
        try {
            const { data: aa } = await api.get(`/report-templates/scope/ad-accounts?platform=${platform}`);
            setAdAccounts(aa || []);
            const aaFilter = aaIds.filter(Boolean).join(",");
            const { data: camps } = await api.get(
                `/report-templates/scope/campaigns?platform=${platform}${aaFilter ? `&ad_account_ids=${aaFilter}` : ""}`,
            );
            setCampaigns(camps || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load scope");
        }
    }, []);

    // Initial load
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                if (isEdit) {
                    const { data } = await api.get(`/report-templates/${templateId}`);
                    setForm({
                        name: data.name,
                        platform: data.platform,
                        ad_account_ids: data.ad_account_ids || [],
                        campaign_ids: data.campaign_ids || [],
                        metrics: data.metrics || [],
                        breakdowns: data.breakdowns?.length ? data.breakdowns : ["none"],
                        date_range_type: data.date_range_type,
                        custom_start_date: data.custom_start_date || "",
                        custom_end_date: data.custom_end_date || "",
                        formats: data.formats || ["csv"],
                        visibility: data.visibility,
                    });
                    await loadCatalog(data.platform);
                    await loadScope(data.platform, data.ad_account_ids || []);
                } else {
                    await loadCatalog("meta");
                    await loadScope("meta", []);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [templateId, isEdit, loadCatalog, loadScope]);

    // When platform changes, reload catalog + scope, reset metrics/breakdowns
    const onPlatformChange = async (p) => {
        // Only Meta exposes a breakdown picker in the template modal; other platforms
        // run at campaign-daily grain (matches their Detailed Reports default view).
        const defaultBreakdown = p === "meta" ? ["none"] : [];
        setForm((f) => ({ ...f, platform: p, metrics: [], breakdowns: defaultBreakdown, ad_account_ids: [], campaign_ids: [] }));
        await loadCatalog(p);
        await loadScope(p, []);
    };

    // When ad-account selection changes, reload campaigns filtered by selection
    useEffect(() => {
        if (!catalog) return;
        loadScope(form.platform, form.ad_account_ids);
        // Prune campaign_ids that no longer belong to the selected ad_accounts
        // Skip this on first mount (loadScope handles it)
    }, [form.ad_account_ids, form.platform, catalog, loadScope]);

    const toggle = (arr, key) => (arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key]);

    const canSubmit = useMemo(() => {
        if (!form.name.trim()) return false;
        if (!form.metrics.length) return false;
        if (!form.formats.length) return false;
        if (form.date_range_type === "custom" && (!form.custom_start_date || !form.custom_end_date)) return false;
        return true;
    }, [form]);

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSaving(true);
        try {
            const payload = {
                ...form,
                name: form.name.trim(),
                ad_account_ids: form.ad_account_ids.length ? form.ad_account_ids : null,
                campaign_ids: form.campaign_ids.length ? form.campaign_ids : null,
                breakdowns: form.breakdowns.length ? form.breakdowns : null,
                custom_start_date: form.date_range_type === "custom" ? form.custom_start_date : null,
                custom_end_date: form.date_range_type === "custom" ? form.custom_end_date : null,
            };
            if (isEdit) {
                await api.patch(`/report-templates/${templateId}`, payload);
                toast.success("Template updated");
            } else {
                await api.post("/report-templates", payload);
                toast.success("Template created");
            }
            onSaved && onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="bg-zinc-50 border border-zinc-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-md"
                data-testid="template-editor-modal"
            >
                <div className="sticky top-0 bg-zinc-50 border-b border-zinc-200 px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <div className="overline">Templates</div>
                        <h3 className="font-display text-xl">{isEdit ? "Edit template" : "New template"}</h3>
                    </div>
                    <button type="button" onClick={onClose} className="p-1 text-zinc-500 hover:text-zinc-900" aria-label="Close">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {loading ? (
                    <div className="p-10 text-center text-zinc-500">
                        <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
                    </div>
                ) : (
                    <div className="p-6 space-y-6">
                        {/* Section 1: Basics */}
                        <Section title="1. Basics">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                    <label className="overline">Template name</label>
                                    <input
                                        required
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        placeholder="e.g. Weekly Meta — Client Ready"
                                        className="w-full mt-1 px-3 py-2 bg-white border border-zinc-200 rounded"
                                        data-testid="template-name-input"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="overline">Platform</label>
                                    <select
                                        value={form.platform}
                                        onChange={(e) => onPlatformChange(e.target.value)}
                                        disabled={isEdit}
                                        className="w-full mt-1 px-3 py-2 bg-white border border-zinc-200 rounded disabled:opacity-50"
                                        data-testid="template-platform-select"
                                    >
                                        <option value="meta">Meta</option>
                                        <option value="dv360">DV360</option>
                                        <option value="tiktok">TikTok</option>
                                    </select>
                                    {isEdit && <div className="text-[10px] text-zinc-400 mt-1">Platform cannot be changed after creation.</div>}
                                </div>
                            </div>
                        </Section>

                        {/* Section 2: Scope */}
                        <Section title="2. Scope">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="overline">Ad accounts</label>
                                    <SearchableMultiSelect
                                        options={adAccounts.map((aa) => ({
                                            id: aa.id,
                                            label: aa.name,
                                            suffix: aa.currency || "",
                                        }))}
                                        selectedIds={form.ad_account_ids}
                                        onChange={(ids) => setForm({ ...form, ad_account_ids: ids, campaign_ids: [] })}
                                        placeholder="All ad accounts in your scope"
                                        emptyText="No ad accounts available for this platform."
                                        testId="template-adaccounts"
                                    />
                                    <div className="text-[10px] text-zinc-400 mt-1">Empty = all ad accounts in your scope.</div>
                                </div>
                                <div>
                                    <label className="overline">Campaigns</label>
                                    <SearchableMultiSelect
                                        options={campaigns.map((c) => ({ id: c.id, label: c.name }))}
                                        selectedIds={form.campaign_ids}
                                        onChange={(ids) => setForm({ ...form, campaign_ids: ids })}
                                        placeholder="All campaigns from the selected ad accounts"
                                        emptyText="No campaigns match your ad-account selection."
                                        testId="template-campaigns"
                                    />
                                    <div className="text-[10px] text-zinc-400 mt-1">Empty = all campaigns from the selected ad accounts.</div>
                                </div>
                            </div>
                        </Section>

                        {/* Section 3: Date range */}
                        <Section title="3. Date range">
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="overline">Range</label>
                                    <select
                                        value={form.date_range_type}
                                        onChange={(e) => setForm({ ...form, date_range_type: e.target.value })}
                                        className="w-full mt-1 px-3 py-2 bg-white border border-zinc-200 rounded"
                                        data-testid="template-daterange-select"
                                    >
                                        {DATE_RANGE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                                    </select>
                                </div>
                                {form.date_range_type === "custom" && (
                                    <>
                                        <div>
                                            <label className="overline">Start date</label>
                                            <input type="date" required value={form.custom_start_date} onChange={(e) => setForm({ ...form, custom_start_date: e.target.value })} className="w-full mt-1 px-3 py-2 bg-white border border-zinc-200 rounded" />
                                        </div>
                                        <div>
                                            <label className="overline">End date</label>
                                            <input type="date" required value={form.custom_end_date} onChange={(e) => setForm({ ...form, custom_end_date: e.target.value })} className="w-full mt-1 px-3 py-2 bg-white border border-zinc-200 rounded" />
                                        </div>
                                    </>
                                )}
                            </div>
                        </Section>

                        {/* Section 4: Breakdown (Meta only — comes before Metrics for Meta per user request) */}
                        {catalog?.supports_breakdown && (
                            <Section title="4. Breakdown">
                                <div className="text-[10px] text-zinc-500 mb-2">
                                    Select one or more breakdowns. Each breakdown produces its own sheet (XLSX) or CSV file in the ZIP.
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-1" data-testid="template-breakdowns-list">
                                    {catalog?.breakdowns.map((b) => (
                                        <label key={b.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-100 px-2 py-1.5 rounded">
                                            <input
                                                type="checkbox"
                                                checked={form.breakdowns.includes(b.key)}
                                                onChange={() => {
                                                    // "none" is mutually-exclusive with the specific breakdowns.
                                                    const next = new Set(form.breakdowns);
                                                    if (b.key === "none") {
                                                        next.clear();
                                                        next.add("none");
                                                    } else {
                                                        next.delete("none");
                                                        if (next.has(b.key)) next.delete(b.key);
                                                        else next.add(b.key);
                                                    }
                                                    const arr = Array.from(next);
                                                    setForm({ ...form, breakdowns: arr.length ? arr : ["none"] });
                                                }}
                                                data-testid={`template-breakdown-${b.key}`}
                                            />
                                            <span>{b.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </Section>
                        )}

                        {/* Metrics — Section number depends on whether Breakdown was shown */}
                        <Section title={`${catalog?.supports_breakdown ? "5" : "4"}. Metrics`}>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="template-metrics-grid">
                                {catalog?.metrics.length === 0 && (
                                    <div className="col-span-full text-xs text-zinc-400 italic px-2 py-3">
                                        No metrics available for you on this platform.
                                    </div>
                                )}
                                {catalog?.metrics.map((m) => (
                                    <label key={m.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-100 px-2 py-1.5 rounded">
                                        <input
                                            type="checkbox"
                                            checked={form.metrics.includes(m.key)}
                                            onChange={() => setForm({ ...form, metrics: toggle(form.metrics, m.key) })}
                                            data-testid={`template-metric-${m.key}`}
                                        />
                                        <span>{m.label}</span>
                                    </label>
                                ))}
                            </div>
                            {form.metrics.length === 0 && catalog?.metrics.length > 0 && (
                                <div className="text-xs text-red-500 mt-2">Select at least one metric.</div>
                            )}
                        </Section>

                        {/* Formats */}
                        <Section title={`${catalog?.supports_breakdown ? "6" : "5"}. Formats`}>
                            <div className="flex gap-4" data-testid="template-formats-list">
                                {["csv", "xlsx"].map((f) => (
                                    <label key={f} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-100 px-2 py-1.5 rounded">
                                        <input
                                            type="checkbox"
                                            checked={form.formats.includes(f)}
                                            onChange={() => setForm({ ...form, formats: toggle(form.formats, f) })}
                                            data-testid={`template-format-${f}`}
                                        />
                                        <span className="uppercase">{f}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="text-[10px] text-zinc-400 mt-2">
                                Selecting both formats will download a ZIP containing both files.
                            </div>
                        </Section>
                    </div>
                )}

                <div className="sticky bottom-0 bg-zinc-50 border-t border-zinc-200 px-6 py-3 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-zinc-500">Cancel</button>
                    <button
                        type="submit"
                        disabled={!canSubmit || saving}
                        className="px-4 py-2 rounded text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                        style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }}
                        data-testid="template-save-btn"
                    >
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        {isEdit ? "Save changes" : "Create template"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">{title}</div>
            {children}
        </div>
    );
}

/**
 * SearchableMultiSelect — a compact dropdown-with-search multi-select.
 *
 * Renders a button showing the summary ("3 selected" or the placeholder). Clicking opens
 * a popover with a search box + filtered checkbox list. Click-outside closes it.
 * Options: [{ id, label, suffix? }]
 */
function SearchableMultiSelect({ options, selectedIds, onChange, placeholder, emptyText, testId }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const wrapRef = useRef(null);
    const searchRef = useRef(null);

    useEffect(() => {
        if (!open) { setQ(""); return; }
        const t = setTimeout(() => searchRef.current?.focus(), 30);
        return () => clearTimeout(t);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return options;
        return options.filter((o) => (o.label || "").toLowerCase().includes(needle));
    }, [q, options]);

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const summary = selectedIds.length === 0
        ? placeholder
        : selectedIds.length === 1
            ? (options.find((o) => o.id === selectedIds[0])?.label || `1 selected`)
            : `${selectedIds.length} selected`;

    const toggleOne = (id) => {
        const next = selectedSet.has(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id];
        onChange(next);
    };

    const clearAll = (e) => { e.stopPropagation(); onChange([]); };
    const selectAllFiltered = () => onChange(Array.from(new Set([...selectedIds, ...filtered.map((f) => f.id)])));

    return (
        <div className="relative mt-1" ref={wrapRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-zinc-200 rounded text-left text-sm hover:border-zinc-400 transition-colors"
                data-testid={`${testId}-trigger`}
                title={selectedIds.length === 1 ? (options.find((o) => o.id === selectedIds[0])?.label || "") : (selectedIds.length > 1 ? selectedIds.map((id) => options.find((o) => o.id === id)?.label).filter(Boolean).join(", ") : "")}
            >
                <span className={`flex-1 truncate ${selectedIds.length === 0 ? "text-zinc-400" : "text-zinc-900"}`}>
                    {summary}
                </span>
                {selectedIds.length > 0 && (
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={clearAll}
                        onKeyDown={(e) => { if (e.key === "Enter") clearAll(e); }}
                        className="text-[10px] uppercase text-zinc-400 hover:text-red-500"
                        data-testid={`${testId}-clear`}
                    >
                        clear
                    </span>
                )}
                <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    className="absolute z-30 mt-1 w-full bg-white border border-zinc-200 rounded shadow-lg"
                    data-testid={`${testId}-popover`}
                >
                    <div className="p-2 border-b border-zinc-100 flex items-center gap-2">
                        <Search className="h-4 w-4 text-zinc-400" />
                        <input
                            ref={searchRef}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search…"
                            className="flex-1 text-sm outline-none bg-transparent"
                            data-testid={`${testId}-search`}
                        />
                        {q && filtered.length > 0 && (
                            <button
                                type="button"
                                onClick={selectAllFiltered}
                                className="text-[10px] uppercase text-zinc-500 hover:wl-accent-text"
                                data-testid={`${testId}-select-all`}
                            >
                                Select all
                            </button>
                        )}
                    </div>
                    <div className="max-h-56 overflow-y-auto py-1" data-testid={`${testId}-options`}>
                        {options.length === 0 && (
                            <div className="text-xs text-zinc-400 italic p-3">{emptyText}</div>
                        )}
                        {options.length > 0 && filtered.length === 0 && (
                            <div className="text-xs text-zinc-400 italic p-3">No matches for &quot;{q}&quot;.</div>
                        )}
                        {filtered.map((o) => {
                            const active = selectedSet.has(o.id);
                            return (
                                <button
                                    type="button"
                                    key={o.id}
                                    onClick={() => toggleOne(o.id)}
                                    title={o.label}
                                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-zinc-50 ${active ? "bg-orange-50/60" : ""}`}
                                    data-testid={`${testId}-option-${o.id}`}
                                >
                                    <span className={`h-4 w-4 flex items-center justify-center rounded border ${active ? "bg-[var(--wl-primary-hex)] border-[var(--wl-primary-hex)]" : "border-zinc-300 bg-white"}`}>
                                        {active && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                    </span>
                                    <span className="flex-1 truncate">{o.label}</span>
                                    {o.suffix && <span className="text-[10px] text-zinc-400">{o.suffix}</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
